import { gql } from '@apollo/client';

const CHILD_QUESTION_FIELDS = `
  _id
  title
  answerExplanation
  answerExplanationWithPinyin {
    html
    spacedPinyin
    audioUrl
  }
  autoGraderKeywords
  gradingSettings {
    strategies {
      strategyName
      sampleAnswers
    }
  }
  questionData {
    questionHeader
    correctOptions
    options
    optionAudioClips
    exampleOption
    shortAnswerType
    tableValues
    tableHeaders
    answerHeader
    subTypeMeta
  }
  meeoppLevelId
  meeoppLevel {
    _id
    levelValue
    displayName
  }
  coreWordIds
  coreWords {
    _id
    text
  }
  contentTagIds
  contentTags {
    _id
    title
    displayTitle
    category
    parentId
  }
  scopeTagIds
  scopeTags {
    _id
    title
    displayTitle
    category
    parentId
  }
  languageConceptTagIds
  languageConceptTags {
    _id
    title
    displayTitle
    category
    parentId
  }
  focusTagsIds
  focusTags {
    _id
    title
    displayTitle
    category
    parentId
  }
  responseFormatTagId
  responseFormatTag {
    _id
    title
    displayTitle
    category
    parentId
  }
  expressionMethodTagIds
  expressionMethodTags {
    _id
    title
    displayTitle
    category
    parentId
  }
`;

const CHILD_QUESTION_SEGMENT = `
  childQuestions {
    ${CHILD_QUESTION_FIELDS}
    childQuestionIds
    childQuestions {
      ${CHILD_QUESTION_FIELDS}
    }
  }
`;

const CHILD_QUESTION_SEGMENT_ORIGINAL = `
childQuestions(isTranslated: false) {
  ${CHILD_QUESTION_FIELDS}
  childQuestionIds
  childQuestions(isTranslated: false) {
    ${CHILD_QUESTION_FIELDS}
  }
}
`;

const WYSIWYG_CONTENT = `
  html
  spacedPinyin
  audioUrl
`;

const getBaseQuestionFields = (options = { isTranslated: true }) => `
  _id
  order
  quizId
  hint {
    content
    url
    type
  }
  media {
    url
    type
    transcript
  }
  tags
  meeoppLevelId
  meeoppLevel {
    _id
    levelValue
    displayName
  }
  coreWordIds
  coreWords {
    _id
    text
  }
  contentTagIds
  contentTags {
    _id
    title
    displayTitle
    category
    parentId
  }
  scopeTagIds
  scopeTags {
    _id
    title
    displayTitle
    category
    parentId
  }
  languageConceptTagIds
  languageConceptTags {
    _id
    title
    displayTitle
    category
    parentId
  }
  focusTagsIds
  focusTags {
    _id
    title
    displayTitle
    category
    parentId
  }
  responseFormatTagId
  responseFormatTag {
    _id
    title
    displayTitle
    category
    parentId
  }
  expressionMethodTagIds
  expressionMethodTags {
    _id
    title
    displayTitle
    category
    parentId
  }
  questionType
  questionData {
    options
    optionsWithFileKey {
      key
      url
    }
    optionsComplex {
      type
      text {
      ${WYSIWYG_CONTENT}
      }
      gifFileURL
      imageFileURL
      audioFileURL
      liveFeedback
    }
    optionAudioClips
    optionAudioClipsWithFileKeys {
      key
      url
    }
    correctOptions
    tableHeaders
    tableValues
    questionHeader
    answerHeader
    exampleOption
    shortAnswerType
    subTypeMeta
    answerHeaders
  }
  title
  childQuestionIds
  ${options.isTranslated ? CHILD_QUESTION_SEGMENT : CHILD_QUESTION_SEGMENT_ORIGINAL}
  answerExplanation
  answerExplanationWithPinyin {
    html
    spacedPinyin
    audioUrl
  }
  autoGraderKeywords
  example
  learningSubject
  titleComplex {
    type
    text {
      ${WYSIWYG_CONTENT}
    }
    gifFileURL
    imageFileURL
    audioFileURL
  }
  gradingSettings {
    strategies {
      strategyName
      sampleAnswers
      rubricId
      aiPromptContext
      evalStrategyId
      correctAnswerPrompt
      incorrectAnswerPrompt
      directPromptChecklist {
        prompt
        minScore
        maxScore
        scoreStep
      }
    }
  }
`;

export const GENERATE_ANSWER_OPTION_AUDIO = gql`
  query GenerateAnswerOptionAudio($optionText: String!) {
    generateAnswerOptionAudio(optionText: $optionText) {
      key
      url
    }
  }
`;

export const QUESTION_FIELDS = getBaseQuestionFields();
export const QUESTION_FIELDS_ORIGINAL = getBaseQuestionFields({ isTranslated: false });

export const UPDATE_QUESTION = gql`
  mutation UpdateQuestion($questionId: MongoObjectId!, $input: QuestionInput!, $gradingSettingsInput: QuestionGradingSettingsInput) {
    updateQuestion(questionId: $questionId, input: $input, gradingSettingsInput: $gradingSettingsInput) {
      ${QUESTION_FIELDS}
    }    
  }
`;

export const CREATE_QUESTION = gql`
  mutation CreateQuestion($input: QuestionInput!, $questionSet: String, $gradingSettingsInput: QuestionGradingSettingsInput) {
    createQuestion(input: $input, questionSet: $questionSet, gradingSettingsInput: $gradingSettingsInput) {
      ${QUESTION_FIELDS}
    }    
  }
`;

export const GENERATE_MULTI_CHOICE_LIVE_FEEDBACK = gql`
  query GenerateMultiChoiceLiveFeedback($input: GenerateMCQLiveFeedbackInput!) {
    generateMultiChoiceLiveFeedback(input: $input) {
      option
      reason
    }
  }
`;

export const FETCH_EVALUATION_STRATEGIES = gql`
  query FetchEvaluationStrategies($strategyType: String!) {
    evaluationStrategies(strategyType: $strategyType) {
      id
      name
    }
  }
`;
